import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { DEFAULT_CONTENT_LOCALE, type LocaleOption } from '@course-authoring/shared-types';
import { useAuth } from '../auth/AuthContext';
import {
  ENGLISH_US_LOCALE_OPTION,
  useContentLocales,
} from '../api/contentLocaleApi';
import { normalizeAuthoringLocale } from '../utils/contentLocale';

interface LanguageSelectionState {
  userId: string | undefined;
  locale: string;
}

export interface LanguageContextValue {
  selectedLocale: string;
  setSelectedLocale: (locale: string) => void;
  registerLocaleChangeGuard: (
    guard: () => boolean | Promise<boolean>,
  ) => () => void;
  languageOptions: LocaleOption[];
  selectedLanguageLabel: string;
  isLanguageCatalogLoading: boolean;
  isLanguageCatalogError: boolean;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

interface LanguageContextProviderProps {
  children: ReactNode;
}

const storageKey = (userId: string | undefined): string | null =>
  userId ? `authorai.languageContext.${userId}` : null;

function readStoredLocale(userId: string | undefined): string {
  const key = storageKey(userId);
  if (!key) return DEFAULT_CONTENT_LOCALE;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? normalizeAuthoringLocale(raw) ?? DEFAULT_CONTENT_LOCALE : DEFAULT_CONTENT_LOCALE;
  } catch {
    return DEFAULT_CONTENT_LOCALE;
  }
}

function writeStoredLocale(userId: string | undefined, locale: string): void {
  const key = storageKey(userId);
  if (!key) return;
  try {
    window.localStorage.setItem(key, locale);
  } catch {
    // Ignore storage failures; the provider retains the in-memory selection.
  }
}

export function LanguageContextProvider({ children }: LanguageContextProviderProps) {
  const { user } = useAuth();
  const userId = user?.id;
  const catalogQuery = useContentLocales(Boolean(userId));
  const languageOptions = catalogQuery.data ?? [ENGLISH_US_LOCALE_OPTION];
  const [selection, setSelection] = useState<LanguageSelectionState>(() => ({
    userId,
    locale: readStoredLocale(userId),
  }));
  const localeChangeGuardRef = useRef<(() => boolean | Promise<boolean>) | null>(null);
  const localeTransitionIdRef = useRef(0);
  const selectedLocale = selection.userId === userId
    ? selection.locale
    : DEFAULT_CONTENT_LOCALE;

  useEffect(() => {
    setSelection({ userId, locale: readStoredLocale(userId) });
  }, [userId]);

  useEffect(() => {
    if (!userId || selection.userId !== userId || !catalogQuery.isSuccess) return;
    if (languageOptions.some((option) => option.locale === selection.locale)) return;
    writeStoredLocale(userId, DEFAULT_CONTENT_LOCALE);
    setSelection({ userId, locale: DEFAULT_CONTENT_LOCALE });
  }, [catalogQuery.isSuccess, languageOptions, selection, userId]);

  const setSelectedLocale = useCallback((value: string) => {
    const locale = normalizeAuthoringLocale(value);
    if (!locale) return;
    if (catalogQuery.isSuccess && !languageOptions.some((option) => option.locale === locale)) return;
    const commit = () => {
      writeStoredLocale(userId, locale);
      setSelection({ userId, locale });
    };
    const guard = localeChangeGuardRef.current;
    if (!guard) {
      commit();
      return;
    }
    const transitionId = ++localeTransitionIdRef.current;
    void Promise.resolve()
      .then(() => guard())
      .then((allowed) => {
        if (allowed && transitionId === localeTransitionIdRef.current) commit();
      })
      .catch(() => undefined);
  }, [catalogQuery.isSuccess, languageOptions, userId]);

  const registerLocaleChangeGuard = useCallback((
    guard: () => boolean | Promise<boolean>,
  ) => {
    localeChangeGuardRef.current = guard;
    return () => {
      if (localeChangeGuardRef.current === guard) localeChangeGuardRef.current = null;
    };
  }, []);

  const selectedLanguageLabel = useMemo(
    () => languageOptions.find((option) => option.locale === selectedLocale)?.label
      ?? (catalogQuery.isLoading ? 'Loading language…' : 'Language unavailable'),
    [catalogQuery.isLoading, languageOptions, selectedLocale]
  );

  const value = useMemo<LanguageContextValue>(
    () => ({
      selectedLocale,
      setSelectedLocale,
      registerLocaleChangeGuard,
      languageOptions,
      selectedLanguageLabel,
      isLanguageCatalogLoading: catalogQuery.isLoading,
      isLanguageCatalogError: catalogQuery.isError,
    }),
    [
      catalogQuery.isError,
      catalogQuery.isLoading,
      languageOptions,
      selectedLanguageLabel,
      selectedLocale,
      registerLocaleChangeGuard,
      setSelectedLocale,
    ]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguageContext(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguageContext must be used within a LanguageContextProvider');
  }
  return context;
}
